<template>
    <div v-if="isVisible" class="modal-overlay">
        <div class="modal-container" @click.stop>
            <div class="modal-header">
                <h2 class="modal-title">고객 등록</h2>
                <button class="close-button" @click="closeModal">×</button>
            </div>

            <div class="modal-body">
                <!-- 왼쪽 섹션: 고객 등록 폼 -->
                <div class="left-section">
                    <h3 class="section-title">
                        📝 고객 정보
                    </h3>

                    <div class="registration-form" :class="{ 'blurred': isTestUser }">
                        <div class="customer-type-tabs">
                            <button v-for="type in customerTypes" :key="type.value"
                                :class="{ active: selectedCustomerType === type.value }"
                                @click="selectCustomerType(type.value)" :data-type="type.value"
                                class="customer-type-tab">
                                {{ type.label }}
                            </button>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="beneficiaryName">
                                    고객명
                                </label>
                                <input type="text" id="beneficiaryName" v-model="formData.name" placeholder="고객명을 입력하세요"
                                    :class="{ 'blurred': isTestUser, 'error': !formData.name.trim() && showValidation }">
                            </div>
                            <div class="form-group">
                                <label for="recognitionNumber">
                                    주민번호
                                </label>
                                <input type="text" id="recognitionNumber" v-model="formData.number"
                                    placeholder="주민번호를 입력하세요"
                                    :class="{ 'blurred': isTestUser, 'error': !formData.number.trim() && showValidation }">
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="benefitType">구분</label>
                                <select id="benefitType" v-model="formData.target" :class="{ 'blurred': isTestUser }">
                                    <option value="">구분을 선택하세요</option>
                                    <option value="일반">일반</option>
                                    <option value="기초">기초</option>
                                    <option value="감경">감경</option>
                                    <option value="의료급여">의료급여</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label for="manager">담당자</label>
                                <input type="text" id="manager" v-model="formData.manager" placeholder="담당자를 입력하세요"
                                    :class="{ 'blurred': isTestUser }">
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group full-width">
                                <label for="address">
                                    주소1
                                </label>
                                <div class="address-input-group">
                                    <input type="text" id="address" v-model="formData.address" placeholder="주소1을 입력하세요"
                                        readonly
                                        :class="{ 'blurred': isTestUser, 'error': !formData.address.trim() && showValidation }">
                                    <button class="address-search-btn"
                                        @click="searchAddress('address', 'addressDetail')">
                                        주소 검색
                                    </button>
                                </div>
                                <input type="text" id="addressDetail" v-model="formData.address_detail"
                                    placeholder="상세주소1을 입력하세요" style="margin-top: 8px;"
                                    :class="{ 'blurred': isTestUser }">
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group full-width">
                                <label for="address2">주소2</label>
                                <div class="address-input-group">
                                    <input type="text" id="address2" v-model="formData.address2"
                                        placeholder="주소2를 입력하세요" readonly :class="{ 'blurred': isTestUser }">
                                    <button class="address-search-btn"
                                        @click="searchAddress('address2', 'addressDetail2')">
                                        주소 검색
                                    </button>
                                </div>
                                <input type="text" id="addressDetail2" v-model="formData.address_detail2"
                                    placeholder="상세주소2를 입력하세요" style="margin-top: 8px;"
                                    :class="{ 'blurred': isTestUser }">
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="guardianName">보호자명</label>
                                <input type="text" id="guardianName" v-model="formData.guard_name"
                                    placeholder="보호자명을 입력하세요" :class="{ 'blurred': isTestUser }">
                            </div>
                            <div class="form-group">
                                <label for="guardianRelation">보호자 관계</label>
                                <select id="guardianRelation" v-model="formData.guard_target"
                                    :class="{ 'blurred': isTestUser }">
                                    <option value="">관계를 선택하세요</option>
                                    <option value="본인">본인</option>
                                    <option value="배우자">배우자</option>
                                    <option value="자녀">자녀</option>
                                    <option value="부모">부모</option>
                                    <option value="기타">기타</option>
                                </select>
                            </div>
                        </div>

                        <div class="form-row">
                            <div class="form-group">
                                <label for="phone1">
                                    연락처1
                                </label>
                                <input type="tel" id="phone1" v-model="formData.phone1" placeholder="연락처1을 입력하세요"
                                    :class="{ 'blurred': isTestUser, 'error': !formData.phone1.trim() && showValidation }">
                            </div>
                            <div class="form-group">
                                <label for="phone2">연락처2</label>
                                <input type="tel" id="phone2" v-model="formData.phone2" placeholder="연락처2를 입력하세요"
                                    :class="{ 'blurred': isTestUser }">
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 오른쪽 섹션: 파일 업로드 -->
                <div class="right-section">
                    <h3 class="section-title">
                        📁 파일 관리
                    </h3>

                    <div class="file-upload-area" @click="triggerFileUpload" @dragover.prevent="handleDragOver"
                        @dragleave.prevent="handleDragLeave" @drop.prevent="handleDrop" :class="{
                            dragover: isDragOver
                        }">
                        <div class="upload-icon">📤</div>
                        <p class="upload-text">
                            파일을 선택하세요
                        </p>
                        <p class="upload-subtext">
                            클릭하거나 파일을 드래그하여 선택
                        </p>
                    </div>

                    <input ref="fileInput" type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                        style="display: none" @change="handleFileSelect">

                    <div class="file-list" v-if="uploadedFiles.length > 0">
                        <div class="file-item" v-for="(file, index) in uploadedFiles" :key="index">
                            <div class="file-info">
                                <div class="file-icon">📄</div>
                                <div class="file-details">
                                    <div class="file-name">{{ file.name }}</div>
                                    <div class="file-size">{{ formatFileSize(file.size) }}</div>
                                </div>
                            </div>
                            <div class="file-actions">
                                <button class="action-button delete" @click="removeFile(index)">
                                    X
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="modal-footer">
                <button class="secondary-button" @click="closeModal">취소</button>
                <button class="primary-button" @click="handleSubmit">등록</button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'RegisterRecipientsModal',

    props: {
        isTestUser: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isVisible: true,
            isDragOver: false,
            isUploading: false,
            uploadProgress: 0,
            showValidation: false, // 유효성 검사 표시 여부
            selectedCustomerType: '일반',
            customerTypes: [
                { value: '일반', label: '일반' },
                { value: '산소', label: '산소' },
                { value: '당뇨', label: '당뇨' },
                { value: '보장구', label: '보장구' }
            ],
            formData: {
                name: '',
                number: '',
                target: '',
                address: '',
                address_detail: '',
                address2: '',
                address_detail2: '',
                guard_name: '',
                guard_target: '',
                manager: '',
                phone1: '',
                phone2: ''
            },
            uploadedFiles: [],
            uploadedFileUrls: []
        }
    },

    mounted() {
        // ESC 키 이벤트 리스너 추가
        document.addEventListener('keydown', this.handleKeydown);

        // 부모 컴포넌트 스크롤 방지
        document.body.style.overflow = 'hidden';
    },

    beforeUnmount() {
        // ESC 키 이벤트 리스너 제거
        document.removeEventListener('keydown', this.handleKeydown);

        // 부모 컴포넌트 스크롤 복원
        document.body.style.overflow = '';
    },

    methods: {
        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.closeModal();
            }
        },

        selectCustomerType(type) {
            this.selectedCustomerType = type;
        },

        searchAddress(addressFieldId, detailFieldId) {
            new window.daum.Postcode({
                oncomplete: (data) => {
                    if (addressFieldId === 'address') {
                        this.formData.address = data.address;
                    } else if (addressFieldId === 'address2') {
                        this.formData.address2 = data.address;
                    }
                }
            }).open();
        },

        async validateForm() {
            this.showValidation = true;

            const trimmed = {
                name: this.formData.name ? this.formData.name.trim() : null,
                number: this.formData.number ? this.formData.number.trim() : null,
                target: this.formData.target ? this.formData.target.trim() : null,
                address: this.formData.address ? this.formData.address.trim() : null,
                address_detail: this.formData.address_detail ? this.formData.address_detail.trim() : null,
                address2: this.formData.address2 ? this.formData.address2.trim() : null,
                address_detail2: this.formData.address_detail2 ? this.formData.address_detail2.trim() : null,
                guard_name: this.formData.guard_name ? this.formData.guard_name.trim() : null,
                guard_target: this.formData.guard_target ? this.formData.guard_target.trim() : null,
                manager: this.formData.manager ? this.formData.manager.trim() : null,
                phone1: this.formData.phone1 ? this.formData.phone1.trim() : null,
                phone2: this.formData.phone2 ? this.formData.phone2.trim() : null,
                customerType: this.selectedCustomerType
            };

            const hasAny =
                Object.values(trimmed).some(v => v && String(v).trim().length > 0);

            if (!hasAny) {
                await this.$swal.fire({
                    title: '입력 안내',
                    text: '최소 한 개 이상의 항목을 입력해주세요.',
                    icon: 'warning',
                    confirmButtonText: '확인',
                    confirmButtonColor: '#0f4c5c'
                });
                return false;
            }

            return trimmed;
        },

        async handleSubmit() {
            const formValues = await this.validateForm();
            if (formValues) {
                // 파일 업로드 먼저 처리
                if (this.uploadedFiles.length > 0) {
                    const uploadSuccess = await this.uploadFiles();
                    if (!uploadSuccess) {
                        return; // 파일 업로드 실패 시 등록 중단
                    }
                }

                // 고객 등록 처리
                const success = await this.registerRecipients(formValues);
                if (success) {
                    this.$emit('registration-completed');
                }
            }
        },

        closeModal() {
            this.$emit('close');
        },

        resetForm() {
            this.showValidation = false; // 유효성 검사 표시 초기화
            this.selectedCustomerType = '일반';
            this.formData = {
                name: '',
                number: '',
                target: '',
                address: '',
                address_detail: '',
                address2: '',
                address_detail2: '',
                guard_name: '',
                guard_target: '',
                manager: '',
                phone1: '',
                phone2: ''
            };
            this.uploadedFiles = [];
        },

        // 파일 업로드 관련 메서드들
        triggerFileUpload() {
            this.$refs.fileInput.click();
        },

        handleFileSelect(event) {
            const files = Array.from(event.target.files);
            this.addFilesToLocalList(files);
        },

        handleDragOver(event) {
            this.isDragOver = true;
        },

        handleDragLeave(event) {
            this.isDragOver = false;
        },

        handleDrop(event) {
            this.isDragOver = false;
            const files = Array.from(event.dataTransfer.files);
            this.addFilesToLocalList(files);
        },

        // 파일을 로컬 목록에 추가 (업로드하지 않음)
        addFilesToLocalList(files) {
            files.forEach(file => {
                // 파일 크기 제한 (10MB)
                if (file.size > 10 * 1024 * 1024) {
                    this.$swal.fire({
                        title: '파일 크기 초과',
                        text: `${file.name} 파일이 너무 큽니다. (최대 10MB)`,
                        icon: 'warning',
                        confirmButtonText: '확인',
                        confirmButtonColor: '#0f4c5c'
                    });
                    return;
                }

                // 중복 파일 체크
                const isDuplicate = this.uploadedFiles.some(f => f.name === file.name && f.size === file.size);
                if (isDuplicate) {
                    this.$swal.fire({
                        title: '중복 파일',
                        text: `${file.name} 파일이 이미 추가되었습니다.`,
                        icon: 'warning',
                        confirmButtonText: '확인',
                        confirmButtonColor: '#0f4c5c'
                    });
                    return;
                }

                // 로컬 파일 목록에 추가
                this.uploadedFiles.push(file);
            });

            // 파일 입력 초기화
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        // 실제 파일 업로드 처리
        async uploadFiles() {
            if (!this.uploadedFiles.length) return true;

            this.isUploading = true;
            this.uploadProgress = 0;

            try {
                const uploadPromises = this.uploadedFiles.map(async (file, index) => {
                    const formData = new FormData();
                    formData.append('file', file);

                    // 백엔드가 기대하는 beneficiaryName을 별도로 추가
                    formData.append('beneficiaryName', this.formData.name.trim());

                    const metadata = {
                        originalName: file.name,
                        uploadDate: new Date().toISOString(),
                        customerName: this.formData.name.trim(),
                        customerType: this.selectedCustomerType
                    };
                    formData.append('metadata', JSON.stringify(metadata));

                    const response = await this.$axios.post(
                        `${this.$apiUrls.hanqmedi}/documents/upload`,
                        formData,
                        {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            },
                            onUploadProgress: (progressEvent) => {
                                const progress = Math.round(
                                    ((index + progressEvent.loaded / progressEvent.total) / this.uploadedFiles.length) * 100
                                );
                                this.uploadProgress = progress;
                            }
                        }
                    );

                    if (!response.data.success) {
                        throw new Error(`${file.name}: ${response.data.message}`);
                    }

                    return response.data;
                });

                await Promise.all(uploadPromises);

                this.$swal.fire({
                    title: '파일 업로드 완료',
                    text: `${this.uploadedFiles.length}개 파일이 성공적으로 업로드되었습니다.`,
                    icon: 'success',
                    confirmButtonText: '확인',
                    confirmButtonColor: '#0f4c5c',
                    timer: 2000,
                    showConfirmButton: false
                });

                return true;

            } catch (error) {
                console.error('파일 업로드 실패:', error);
                this.$swal.fire({
                    title: '파일 업로드 실패',
                    text: error.message || '파일 업로드 중 오류가 발생했습니다.',
                    icon: 'error',
                    confirmButtonText: '확인',
                    confirmButtonColor: '#0f4c5c'
                });
                return false;
            } finally {
                this.isUploading = false;
                this.uploadProgress = 0;
            }
        },

        removeFile(index) {
            this.uploadedFiles.splice(index, 1);
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        async registerRecipients(formValues) {
            let recipientData = null;

            try {
                recipientData = {
                    name: formValues.name,
                    number: formValues.number,
                    cnum: formValues.cnum || null,
                    target: formValues.target,
                    address: formValues.address,
                    address_detail: formValues.address_detail,
                    address2: formValues.address2,
                    address_detail2: formValues.address_detail2,
                    guard_name: formValues.guard_name,
                    guard_target: formValues.guard_target,
                    manager: formValues.manager,
                    phone1: formValues.phone1,
                    phone2: formValues.phone2,
                    type: '비급여',
                    customer_type: formValues.customerType,
                    companykey: this.$globalState.company_key
                };

                console.log('전송할 데이터:', recipientData);

                const response = await this.$axios({
                    method: 'post',
                    url: `${this.$apiUrls.hanqmedi}/registerRecipient`,
                    data: recipientData,
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('token')}`,
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.success) {
                    await this.$swal.fire({
                        title: '등록 완료',
                        text: `"${recipientData.name}"고객이 성공적으로 등록되었습니다.`,
                        icon: 'success',
                        confirmButtonText: '확인',
                        confirmButtonColor: '#0f4c5c'
                    });

                    this.resetForm();
                    return true;
                } else {
                    throw new Error(response.data?.message || '고객 등록에 실패했습니다.');
                }

            } catch (error) {
                console.error('고객 등록 오류:', {
                    error: {
                        message: error.message,
                        response: error.response?.data,
                        status: error.response?.status
                    },
                    timestamp: new Date().toISOString(),
                    recipientData: recipientData
                });

                let errorMessage = '고객 등록 중 오류가 발생했습니다.';
                let errorTitle = '등록 실패';

                if (error.response?.data?.message) {
                    errorMessage = error.response.data.message;
                } else if (error.message) {
                    errorMessage = error.message;
                }

                if (error.response?.status === 400) {
                    errorTitle = '입력 오류';
                } else if (error.response?.status === 401) {
                    errorTitle = '인증 오류';
                } else if (error.response?.status === 500) {
                    errorTitle = '서버 오류';
                }

                this.$swal.fire({
                    title: errorTitle,
                    text: errorMessage,
                    icon: 'error',
                    confirmButtonText: '확인',
                    confirmButtonColor: '#0f4c5c',
                    showCloseButton: true
                });

                return false;
            }
        }
    },
}
</script>

<style scoped>
/* 모달 오버레이 */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
}

/* 모달 컨테이너 */
.modal-container {
    background: white;
    border-radius: 12px;
    width: 95%;
    max-width: 1200px;
    max-height: 85vh;
    overflow: hidden;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
    display: flex;
    flex-direction: column;
}

/* 모달 헤더 */
.modal-header {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #e5e7eb;
    position: relative;
}

/* 에러 상태 스타일 */
.form-group input.error,
.form-group select.error {
    border-color: #dc2626;
    background-color: #fef2f2;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.form-group input.error:focus,
.form-group select.error:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.2);
}

/* 필수 항목 라벨 강조 */
.form-group label:has(.required) {
    color: #1f2937;
    font-weight: 700;
}

.modal-title {
    margin: 0;
    font-size: 25px;
    font-weight: 600;
    color: #1f2937;
    text-align: center;
}

.close-button {
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #6b7280;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all 0.2s ease;
    position: absolute;
    right: 24px;
    top: 50%;
    transform: translateY(-50%);
}

.close-button:hover {
    background: #f3f4f6;
    color: #374151;
}

/* 모달 바디 */
.modal-body {
    display: flex;
    flex: 1;
    overflow: hidden;
}

/* 왼쪽 섹션 */
.left-section {
    flex: 1;
    padding: 24px;
    border-right: 1px solid #e5e7eb;
    overflow-y: auto;
}

.left-section::-webkit-scrollbar {
    width: 6px;
}

.left-section::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.left-section::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.left-section::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* 오른쪽 섹션 */
.right-section {
    flex: 1;
    padding: 24px;
    background: #f8fafc;
    overflow-y: auto;
}

.right-section::-webkit-scrollbar {
    width: 6px;
}

.right-section::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
}

.right-section::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
}

.right-section::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}

/* 섹션 제목 */
.section-title {
    margin: 0 0 20px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1f2937;
    display: flex;
    align-items: center;
    gap: 8px;
}

/* 등록 폼 */
.registration-form {
    max-height: none;
    max-width: none;
    padding: 0;
}

/* 고객 유형 탭 */
.customer-type-tabs {
    display: flex;
    background: #f8fafc;
    border-radius: 12px;
    padding: 4px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.customer-type-tab {
    flex: 1;
    padding: 12px 16px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.customer-type-tab:hover {
    color: #0f4c5c;
    background: rgba(15, 76, 92, 0.05);
}

.customer-type-tab.active {
    color: #ffffff;
    background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
    box-shadow: 0 4px 12px rgba(15, 76, 92, 0.3);
}

.customer-type-tab.active::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #1a6b7a;
}

/* 폼 행 */
.form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 20px;
}

/* 폼 그룹 */
.form-group {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.form-group.full-width {
    flex: none;
    width: 100%;
}

.form-group label {
    font-size: 15px;
    font-weight: 600;
    color: #374151;
    margin-bottom: 6px;
    display: block;
}

.form-group input,
.form-group select {
    padding: 12px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 14px;
    transition: all 0.2s ease;
    background: #ffffff;
    color: #1f2937;
}

.form-group input:focus,
.form-group select:focus {
    outline: none;
    border-color: #0f4c5c;
}

.form-group input::placeholder,
.form-group select::placeholder {
    color: #9ca3af;
}

.form-group input[readonly] {
    background: #f9fafb;
    color: #6b7280;
    cursor: pointer;
}

.form-group input[readonly]:hover {
    background: #f3f4f6;
}

/* 고객 유형 탭 */
.customer-type-tabs {
    display: flex;
    background: #f8fafc;
    border-radius: 12px;
    padding: 4px;
    margin-bottom: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.customer-type-tab {
    flex: 1;
    padding: 12px 16px;
    text-align: center;
    font-size: 14px;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.3s ease;
    position: relative;
}

.customer-type-tab:hover {
    background: rgba(15, 76, 92, 0.05);
}

/* 일반 탭 */
.customer-type-tab[data-type="일반"]:hover {
    color: #0d6efd;
    background: rgba(13, 110, 253, 0.05);
}

.customer-type-tab[data-type="일반"].active {
    color: #ffffff;
    background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
    box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
}

.customer-type-tab[data-type="일반"].active::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #0b5ed7;
}

/* 산소 탭 */
.customer-type-tab[data-type="산소"]:hover {
    color: #dc3545;
    background: rgba(220, 53, 69, 0.05);
}

.customer-type-tab[data-type="산소"].active {
    color: #ffffff;
    background: linear-gradient(135deg, #dc3545 0%, #b02a37 100%);
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.3);
}

.customer-type-tab[data-type="산소"].active::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #b02a37;
}

/* 당뇨 탭 */
.customer-type-tab[data-type="당뇨"]:hover {
    color: rgb(21, 142, 163);
    background: rgba(21, 142, 163, 0.05);
}

.customer-type-tab[data-type="당뇨"].active {
    color: #ffffff;
    background: linear-gradient(135deg, rgb(21, 142, 163) 0%, rgb(17, 114, 131) 100%);
    box-shadow: 0 4px 12px rgba(21, 142, 163, 0.3);
}

.customer-type-tab[data-type="당뇨"].active::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgb(17, 114, 131);
}

/* 보장구 탭 */
.customer-type-tab[data-type="보장구"]:hover {
    color: #20c997;
    background: rgba(32, 201, 151, 0.05);
}

.customer-type-tab[data-type="보장구"].active {
    color: #ffffff;
    background: linear-gradient(135deg, #20c997 0%, #1aa179 100%);
    box-shadow: 0 4px 12px rgba(32, 201, 151, 0.3);
}

.customer-type-tab[data-type="보장구"].active::before {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid #1aa179;
}

/* 주소 입력 그룹 */
.address-input-group {
    display: flex;
    gap: 8px;
    align-items: center;
}

.address-input-group input {
    flex: 1;
}

.address-search-btn {
    padding: 12px 20px;
    background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
    color: white;
    border: none;
    border-radius: 12px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
    box-shadow: 0 4px 15px rgba(15, 76, 92, 0.2);
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-width: 120px;
}

.address-search-btn::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
}

.address-search-btn:hover {
    background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(15, 76, 92, 0.3);
}

.address-search-btn:hover::before {
    left: 100%;
}

.address-search-btn:active {
    transform: translateY(0);
    box-shadow: 0 2px 10px rgba(15, 76, 92, 0.2);
}

.address-search-btn:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(15, 76, 92, 0.1), 0 4px 15px rgba(15, 76, 92, 0.2);
}

/* 파일 업로드 영역 */
.file-upload-area {
    border: 2px dashed #cbd5e1;
    border-radius: 12px;
    padding: 40px 20px;
    text-align: center;
    background: white;
    transition: all 0.3s ease;
    cursor: pointer;
    margin-bottom: 24px;
}

.file-upload-area:hover {
    border-color: #0f4c5c;
    background: #f0f9ff;
}

.file-upload-area.dragover {
    border-color: #0f4c5c;
    background: #f0f9ff;
    transform: scale(1.02);
}

.file-upload-area.disabled {
    border-color: #e5e7eb;
    background: #f9fafb;
    cursor: not-allowed;
    opacity: 0.6;
}

.file-upload-area.disabled:hover {
    border-color: #e5e7eb;
    background: #f9fafb;
    transform: none;
}

.upload-icon {
    font-size: 48px;
    color: #cbd5e1;
    margin-bottom: 16px;
    transition: color 0.3s ease;
}

.file-upload-area:hover .upload-icon {
    color: #0f4c5c;
}

.upload-text {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #374151;
}

.upload-subtext {
    margin: 0;
    font-size: 14px;
    color: #6b7280;
}

/* 파일 리스트 */
.file-list {
    margin-top: 24px;
}

.file-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 16px;
    background: white;
    border-radius: 8px;
    margin-bottom: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
}

.file-item:hover {
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
}

.file-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
}

.file-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    color: white;
    background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
}

.file-details {
    flex: 1;
}

.file-name {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 2px;
}

.file-size {
    font-size: 12px;
    color: #6b7280;
}

.file-actions {
    display: flex;
    gap: 8px;
}

.action-button {
    padding: 6px 12px;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.action-button.download {
    background: #f0f9ff;
    color: #0369a1;
}

.action-button.download:hover {
    background: #e0f2fe;
}

.action-button.delete {
    background: #fef2f2;
    color: #dc2626;
}

.action-button.delete:hover {
    background: #fee2e2;
}

/* 모달 푸터 */
.modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

/* 버튼 */
.secondary-button {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    background: #f3f4f6;
    color: #374151;
}

.secondary-button:hover {
    background: #e5e7eb;
}

.primary-button {
    padding: 10px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    border: none;
    background: linear-gradient(135deg, #0f4c5c 0%, #1a6b7a 100%);
    color: white;
}

.primary-button:hover {
    background: linear-gradient(135deg, #1a6b7a 0%, #0f4c5c 100%);
}

/* 블러 효과 */
.blurred {
    filter: blur(4px) !important;
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}

.blurred * {
    pointer-events: auto;
    user-select: auto;
    -webkit-user-select: auto;
    -moz-user-select: auto;
    -ms-user-select: auto;
}
</style>